/* eslint-disable max-len */
import React from 'react';
import BaseComponent from '../baseComponent';
import ArticleTileGrid from '../articleTileGrid/article-tile-grid';
import classNames from 'classnames';
import BottomSearchbox from './searchbox/bottom-searchbox';
import FasBar from '../fas-bar/fas-bar';
import LanguageSwitcher from './language-switcher/language-switcher';
import SuggestionLinks from './suggestion-links/suggestion-links';
import TeaserView from './teaser-view/teaser-view';
import SearchInsteadInfo from './searchbox/search-instead-info';
import '../../Common/customStyles.scss';
import styles from './searchresultpage.scss';
import StickyHeader from '../stickyHeader/stickyHeader';
import Paging from '../paging/paging';
import { ArticleTilesAjaxCaller } from '../articleTileGrid/ArticleTilesAjaxCaller';
import {
    ISearchFilteringResult,
    ISearchResultPageProps,
    ISearchResultPageState,
    SearchResultPagePropsToSdvMapper,
    SearchResultPageType,
} from './searchresultpage.d';
import { GlobalState } from '../globalState/globalState';
import { ArticleListController } from '../globalState/ArticleListController';
import { convertFromServiceToReactWorld } from '../globalState/ServiceSelectedFilterConverter';
import MainSidePanel from '../mainSidePanel/mainSidePanel';
import * as helper from '../../Common/html-helper';
import ProgressBarAnimation from '../progressBarAnimation/progressBarAnimation';
import TouchPointFactory from '../../Common/MPCTouchPointFactory';
import { BookmarkController } from '../globalState/BookmarkController';
import { ScrollLock } from '../../../Helper/scrollLock';
import SortContextProvider from '../globalState/sortContextProvider';
import * as Constants from '../../Common/constants';
import ArticleTileContextProvider from '../globalState/articleTileContext';
import MultipleContextProvider from '../globalState/MultipleContext';
import TabNavigationHelper from '../../Common/tabNavigationHelper';
import CollectionLineContextProvider from '../../Common/contexts/collectionLineContext';
import FlexibleSetListWrapper from './flexible-set-list-wrapper/flexible-set-list-wrapper';
import * as ThirdPartyEvents from './third-party-events/third-party-events';
import { AtsTrackingHelper, ISearchResultEventData, ISearchResultEventItem } from '../../Common/atsTrackingHelper';
import { UrlController } from '../globalState/UrlController';
import { ViewType } from '../../Common/enums';

export default class SearchResultPage extends BaseComponent<ISearchResultPageProps, ISearchResultPageState> {
    private articleTilesAjaxCaller: ArticleTilesAjaxCaller;
    private singleGridRef = React.createRef<HTMLDivElement>();
    private firstGridRef = React.createRef<HTMLDivElement>();
    private secondGridRef = React.createRef<HTMLDivElement>();
    private contentRefForStickyHeader = React.createRef<HTMLDivElement>();
    private touchPointFactory: TouchPointFactory;
    private globalState: GlobalState;
    private bookmarkController: BookmarkController;
    infinityScrollingIsLoading: boolean;
    private searchRef = React.createRef<HTMLDivElement>();
    private historyTileId: string;
    private articleGridDividerHeadline: string;
    private isEmptySearchResultPage: boolean;
    private isRegularSearchResultPage: boolean;
    private isFullAltSearchResultPage: boolean;
    private isTeaserSearchResultPage: boolean;
    private tabNav: TabNavigationHelper;
    private readonly urlController: UrlController;

    constructor(props: ISearchResultPageProps) {
        super(props);
        this.state = {
            salesDesignationViews: SearchResultPagePropsToSdvMapper(this.props),
            pageNo: this.props.searchResultPageProps.pagingInformation?.pageNumber ?? 1,
            waitingForResponse: false,
            totalResultCount: this.props.searchResultPageProps.pagingInformation?.totalResultCount ?? 0,
            totalPageCount: this.props.searchResultPageProps.pagingInformation?.totalPageCount ?? 1,
            isBreadcrumbOnly: false,
            componentMounted: false,
            scrollPosition: 0,
            activeDropDownFilterName: '',
            stickyHeaderDropDownFilterName: '',
            isStickyHeaderVisible: false,
            toggleToUpdateFasBar: false,
            infiniteScrollUpMode: helper.isUrlWithPageQuery(this.props.searchResultPageProps.url),
            pageEndOffset: this.props.searchResultPageProps.pagingInformation?.pageEndOffset ?? 0,
            infiniteScrollUpMinPageNo: this.props.searchResultPageProps.pagingInformation?.pageNumber ?? 1,
            isSearchBottomReached: false,
            hasFlexibleSets: this.props.searchResultPageProps.flexibleSetContext?.flexibleSets?.length > 0,
            flexibleSetContext: this.props.searchResultPageProps.flexibleSetContext,
        };

        this.isEmptySearchResultPage = this.props.searchResultPageProps.type === SearchResultPageType.Empty;
        this.isRegularSearchResultPage = this.props.searchResultPageProps.type === SearchResultPageType.Regular;
        this.isFullAltSearchResultPage = this.props.searchResultPageProps.type === SearchResultPageType.FullAlternativeView;
        this.isTeaserSearchResultPage = this.props.searchResultPageProps.type === SearchResultPageType.TeaserAlternativeView;

        this.urlController = new UrlController(this.props.searchResultPageProps.filterContentViewModel.seoSlug, 
            ViewType.Search, this.props.searchResultPageProps.magicLink);

        // init global state
        const initSelectedFilters = convertFromServiceToReactWorld(
            props.searchResultPageProps.filterContentViewModel.userSelectedFilters,
            props.searchResultPageProps.filterContentViewModel.filterModel);
        this.globalState = new GlobalState(
            this.props.searchResultPageProps.filterContentViewModel.filterModel,
            initSelectedFilters,
            this.props.searchResultPageProps.filterContentViewModel.kiosk,
            this.urlController);
        this.articleTilesAjaxCaller = new ArticleTilesAjaxCaller(this.globalState.Spinner, this.props.searchResultPageProps.magicLink);
        const articleListController = new ArticleListController(
            this.articleTilesAjaxCaller, this.globalState.onNewSearchArticlesLoaded);
        this.globalState.articleListController = articleListController;
        this.onNewArticles = this.onNewArticles.bind(this);
        this.globalState.registerOnSearchArticlesChanged(this.onNewArticles);
        this.tabNav = TabNavigationHelper.instance;

        const salesDesignationViews = SearchResultPagePropsToSdvMapper(this.props);
        if (salesDesignationViews.length > 0)
            this.bookmarkController = new BookmarkController(salesDesignationViews, this.globalState,
        null, this.props.searchResultPageProps.currencyInformation.isoCode);

        this.touchPointFactory = TouchPointFactory.instance;
        this.props.searchResultPageProps.filterContentViewModel.l10n = this.props.l10n;
        this.props.searchResultPageProps.filterContentViewModel.originalSearchTerm =
            this.props.searchResultPageProps.originalSearchTerm;
        if (this.isFullAltSearchResultPage) {
            this.props.searchResultPageProps.filterContentViewModel.searchTerm =
                this.props.searchResultPageProps.articleLists[0].searchString;
        }

        this.handleLoadArticles = this.handleLoadArticles.bind(this);
        this.stickyHeaderVisibleChange = this.stickyHeaderVisibleChange.bind(this);
        this.handleActiveDropDownFilterChange = this.handleActiveDropDownFilterChange.bind(this);
        this.handleSortContextChange = this.handleSortContextChange.bind(this);
        this.infiniteScroll = this.infiniteScroll.bind(this);
    }

    private createTouchpoints(): void {
        // first iteration for mpc tp on after page is loaded
        this.touchPointFactory.createTouchPointsBatch(this.state.salesDesignationViews);
    }

    componentDidMount(): void {
        super.componentDidMount();
        this.setState({ isBreadcrumbOnly: false, componentMounted: true });

        this.articleGridDividerHeadline = this.props.searchResultPageProps
            .filterContentViewModel.isMobile
            ? `${helper.decodeHTML(this.props.l10n.furtherResults)}:`
            : helper.decodeHTML(this.props.l10n.furtherResultsToSearchTerm) + ' ' +
              this.props.l10n.startQuotationMark +
              this.props.searchResultPageProps.filterContentViewModel.searchTerm +
              this.props.l10n.endQuotationMark +
              ' :';

        if (window && window.location && window.location.hash.startsWith('#ats-')) {
            this.historyTileId = window.location.hash;
            window.history.scrollRestoration = 'manual';
            ScrollLock.instance.lock();

            //remove hash
            history.replaceState(null, null, window.location.pathname + window.location.search);
        }

        if (window && window.location && window.location.search.indexOf('page=') >= 0) {
            const params = new URL(window.location.href).searchParams;
            params.delete('page');
            let newUrl = window.location.pathname;
            if (params.size > 0) {
                newUrl += '?' + params.toString();
            }
            history.replaceState(null, null, newUrl);
        }

        this.createTouchpoints();
        if (this.historyTileId && this.historyTileId.length > 0) {
            if (this.state.pageNo > 1) {
                this.infiniteScroll();
            } else {
                setTimeout(() => {
                    ScrollLock.instance.unlock();
                    document.querySelector(this.historyTileId)?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
                    this.historyTileId = '';
                }, 300);
            }
        }

        this.handleMissingTabNavAttOnFirstArticleGridRef();
        this.handleTrackingEvents();
    }

    private infiniteScroll() {
        if (
            this.state.infiniteScrollUpMode && // we have returned from somwhere to ATS
            this.state.infiniteScrollUpMinPageNo > 1 && // there are missing pages still
            !this.infinityScrollingIsLoading // we dont currently wait for an older load
        ) {
            const lastScrollHeight = window.document.body.scrollHeight;
            this.infinityScrollingIsLoading = true;
            this.articleTilesAjaxCaller.loadMoreArticles(
                this.state.infiniteScrollUpMinPageNo - 1,
                this.props.searchResultPageProps.filterContentViewModel.kiosk,
                this.props.searchResultPageProps.filterContentViewModel.viewType,
                false,
                this.props.searchResultPageProps.filterContentViewModel.customerType,
                this.props.searchResultPageProps.filterContentViewModel.enableAISorting
            ).then((data) => {
                this.infinityScrollingIsLoading = false;
                this.setState({
                    salesDesignationViews: data.articles.concat(this.state.salesDesignationViews), //prepend
                    infiniteScrollUpMinPageNo: this.state.infiniteScrollUpMinPageNo - 1,
                }, () => {
                    this.touchPointFactory.createTouchPointsBatch(data.articles);
                });
            }).finally(() => {
                ScrollLock.instance.unlock();
                if (this.historyTileId && this.historyTileId.length > 0) {
                    document.querySelector(this.historyTileId)?.scrollIntoView(
                        { behavior: 'auto', block: 'center', inline: 'center' });
                    this.historyTileId = '';
                } else {
                    // we want to keep the scroll position
                    setTimeout(() => {
                        const heightDifference = window.document.body.scrollHeight - lastScrollHeight;
                        window.scrollBy({ top: heightDifference });
                    }, 100);
                }
            }).catch(() => {
                ScrollLock.instance.unlock();
                this.infinityScrollingIsLoading = false;
            });
        }
    }

    componentWillUnmount(): void {
        ScrollLock.instance.unlock();
    }

    public onNewArticles(result: ISearchFilteringResult) {
        this.setState({
            salesDesignationViews: result.articles,
            totalResultCount: result.totalResultCount,
            totalPageCount: result.totalPageCount,
            pageEndOffset: result.pageEndOffset,
            pageNo: 1,
            hasFlexibleSets: result.flexibleSetContext?.flexibleSets?.length > 0,
            flexibleSetContext: result.flexibleSetContext,
            waitingForResponse: false,
            toggleToUpdateFasBar: !this.state.toggleToUpdateFasBar,
            infiniteScrollUpMode: false // after filtering, page url parameter related element should be removed
        });
        ScrollLock.instance.checkSliderPosition();
    }

    public stickyHeaderVisibleChange(visible: boolean) {
        if (this.state.isStickyHeaderVisible !== visible) {
            const activeFilterDropdownName = this.state.isStickyHeaderVisible ?
                this.state.stickyHeaderDropDownFilterName : this.state.activeDropDownFilterName;
            this.setState({ stickyHeaderDropDownFilterName: visible ? activeFilterDropdownName : '' });
            this.setState({ activeDropDownFilterName: visible ? '' : activeFilterDropdownName });
        }
        this.setState({ isStickyHeaderVisible: visible });
    }

    public handleActiveDropDownFilterChange = (prActiveDropDownFilter: string) => {
        this.setState({
            activeDropDownFilterName: this.state.isStickyHeaderVisible ? '' : prActiveDropDownFilter,
            stickyHeaderDropDownFilterName: this.state.isStickyHeaderVisible ? prActiveDropDownFilter : ''
        });
    }

    private handleSortContextChange(shouldForceScroll) {
        this.globalState.updateArticleList(
            shouldForceScroll,
            this.props.searchResultPageProps.filterContentViewModel.viewType,
            this.props.searchResultPageProps.filterContentViewModel.customerType,
            this.props.searchResultPageProps.filterContentViewModel.enableAISorting);
    }

    public render() {
        const isMspNeeded = this.props.searchResultPageProps.articleLists?.length === 1 &&
            !this.isTeaserSearchResultPage && this.state.componentMounted;
        const shouldHaveFasBar = this.props.searchResultPageProps.articleLists?.length === 1 && (
            this.props.searchResultPageProps.filterContentViewModel?.filterModel &&
            this.state.totalResultCount > 0);
        const isNewsLetterVisible = !(this.props.searchResultPageProps.filterContentViewModel.kiosk ||
            this.props.searchResultPageProps.filterContentViewModel.isMobile)
            && this.state.isStickyHeaderVisible === true && this.state.salesDesignationViews.length > 4;
        const renderNLP = !this.props.searchResultPageProps.filterContentViewModel.isMobile &&
            !this.isEmptySearchResultPage && !this.isTeaserSearchResultPage;
        const showSecondArticleGrid = this.props.searchResultPageProps.filterContentViewModel.isMobile 
            ? this.state.salesDesignationViews.length >= 5
            : this.state.salesDesignationViews.length >= 9;
        const articleGridSplitIndex = this.props.searchResultPageProps.filterContentViewModel.isMobile ? 4 : 8;

        return (
            <CollectionLineContextProvider collectionMappings={this.props.searchResultPageProps.collectionLinesConfig || {}}>
                <SortContextProvider
                    viewType={this.props.searchResultPageProps.filterContentViewModel.viewType}
                    l10n={this.props.searchResultPageProps.filterContentViewModel.l10n}
                    glovesFilter={this.globalState.filterModel.filters?.find((x) => x.name === Constants.GLOVES)}
                    initialSort={this.props.searchResultPageProps.filterContentViewModel.initialSort}
                    defaultSort={this.props.searchResultPageProps.filterContentViewModel.defaultSort}
                    updateArticleList={this.handleSortContextChange}
                    magicLink={this.props.searchResultPageProps.magicLink}
                    enableAISorting={this.props.searchResultPageProps.filterContentViewModel.enableAISorting}
                >
                    <ArticleTileContextProvider
                        l10n={this.props.l10n}
                        cdnUrl={this.props.searchResultPageProps.cdnUrl}
                        culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                        urlController={this.urlController}
                        customerType={this.props.searchResultPageProps.filterContentViewModel.customerType}
                        enableAISorting={this.props.searchResultPageProps.filterContentViewModel.enableAISorting}
                        viewType={this.props.searchResultPageProps.filterContentViewModel.viewType}
                        deliveryTimeTooltip={this.props.searchResultPageProps.filterContentViewModel.filterModel?.filters
                            ?.find((x) => x.name === Constants.DELIVERY_TIME)
                            ?.filterPropertyValues?.[0]?.tooltip ?? null}
                    >
                        <div ref={this.searchRef} className={classNames(styles.searchresultpage, this.props.searchResultPageProps.style)}>
                            {isMspNeeded &&
                                <MainSidePanel
                                    shouldShowProductFinder={false}
                                    productFinderServicesUpAndRunning={false}
                                    filterContentViewModel={this.props.searchResultPageProps.filterContentViewModel}
                                    globalState={this.globalState}
                                    l10n={this.props.l10n}
                                    productFinderKey={''}
                                />
                            }

                            {(shouldHaveFasBar &&
                                <>
                                    <div id='ats_scope-fas-bar' className='fas_scope-fas'>
                                        <MultipleContextProvider>
                                            <FasBar
                                                {...this.props.searchResultPageProps.filterContentViewModel}
                                                dropDownFilterChange={this.handleActiveDropDownFilterChange}
                                                activeDropDownFilterName={this.state.activeDropDownFilterName}
                                                globalState={this.globalState}
                                                toggleToUpdateFasBar={this.state.toggleToUpdateFasBar}
                                                mspOrigin='s_fasBar_1'
                                                magicLink={this.props.searchResultPageProps.magicLink}
                                            />
                                        </MultipleContextProvider>
                                        <ProgressBarAnimation
                                            scrollPosition={this.state.scrollPosition}
                                            pageEndOffset={this.state.pageEndOffset}
                                            salesDesignationViews={this.state.salesDesignationViews}
                                            totalResultCount={this.state.totalResultCount}
                                            infiniteScroll={this.infiniteScroll}
                                        />
                                    </div>

                                    <StickyHeader
                                        isBreadcrumbOnly={this.state.isBreadcrumbOnly}
                                        isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                        isEsApp={this.props.searchResultPageProps.filterContentViewModel.isEsApp}
                                        gridRef={this.contentRefForStickyHeader}
                                        visibleCallBack={this.stickyHeaderVisibleChange}
                                        renderNLP={renderNLP}
                                        nlpIsVisibleByParentRules={isNewsLetterVisible}
                                        isSearchPage={true}
                                    >
                                        <div id='ats_sticky-fas-bar' className='fas_scope-fas'>
                                            <MultipleContextProvider>
                                                <FasBar
                                                    {...this.props.searchResultPageProps.filterContentViewModel}
                                                    dropDownFilterChange={this.handleActiveDropDownFilterChange}
                                                    activeDropDownFilterName={this.state.stickyHeaderDropDownFilterName}
                                                    globalState={this.globalState}
                                                    toggleToUpdateFasBar={this.state.toggleToUpdateFasBar}
                                                    mspOrigin='s_fasBar_2'
                                                    magicLink={this.props.searchResultPageProps.magicLink}
                                                />
                                            </MultipleContextProvider>
                                            <ProgressBarAnimation
                                                scrollPosition={this.state.scrollPosition}
                                                pageEndOffset={this.state.pageEndOffset}
                                                salesDesignationViews={this.state.salesDesignationViews}
                                                totalResultCount={this.state.totalResultCount}
                                                infiniteScroll={this.infiniteScroll}
                                            />
                                        </div>
                                    </StickyHeader>
                                </>
                            )}

                            <div ref={this.contentRefForStickyHeader}>
                                {(this.isEmptySearchResultPage || this.isFullAltSearchResultPage) && this.state.hasFlexibleSets && (
                                    <FlexibleSetListWrapper
                                        flexibleSetContext={this.state.flexibleSetContext}
                                        culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                        portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                        searchTerm={this.isFullAltSearchResultPage 
                                            ? this.props.searchResultPageProps.originalSearchTerm 
                                            : this.props.searchResultPageProps.filterContentViewModel.searchTerm
                                        }
                                        magicLink={this.props.searchResultPageProps.magicLink}
                                        l10n={this.props.l10n}
                                        kiosk={this.props.searchResultPageProps.filterContentViewModel.kiosk}
                                        isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                        isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                        searchPageStyle={this.props.searchResultPageProps.style}
                                    />
                                )}

                                {this.props.searchResultPageProps.searchInsteadInfo &&
                                    <SearchInsteadInfo
                                        {...this.props.searchResultPageProps.searchInsteadInfo}
                                        l10n={this.props.l10n}
                                    />
                                }

                                {this.props.searchResultPageProps.topSuggestionLinks?.suggestionLinks.length > 0 &&
                                    <SuggestionLinks
                                        suggestionLink={this.props.searchResultPageProps.topSuggestionLinks}
                                        isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                        isTablet={this.props.searchResultPageProps.filterContentViewModel.isTablet}
                                        portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                        culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                        position={'top'}
                                        magicLink={this.props.searchResultPageProps.magicLink}
                                        seeAllResultsL10n={this.props.l10n.seeAllResults}
                                    />
                                }

                                {this.isFullAltSearchResultPage && 
                                    <>
                                        <ArticleTileGrid
                                            currencyInformation={this.props.searchResultPageProps.currencyInformation}
                                            viewType={this.props.searchResultPageProps.filterContentViewModel.viewType}
                                            isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                            isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                            culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                            portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                            isFilterDropDownOpen={!!this.state.activeDropDownFilterName}
                                            articles={this.state.salesDesignationViews}
                                            category={null}
                                            l10n={this.props.l10n}
                                            kiosk={this.props.searchResultPageProps.filterContentViewModel.kiosk}
                                            gridRef={this.singleGridRef}
                                            addItemOriginToPdpLink={true}
                                            gridNumber={0}
                                            globalState={this.globalState}
                                            singleArticleView={this.state.salesDesignationViews && this.state.salesDesignationViews.length === 1}
                                            magicLink={this.props.searchResultPageProps.magicLink}
                                        />
                                        {this.state.totalResultCount > 0 &&
                                            <Paging
                                                viewedArticleNumber={this.state.pageEndOffset}
                                                totalArticleCount={this.state.totalResultCount}
                                                waitingForResponse={this.state.waitingForResponse}
                                                onLoadMore={this.handleLoadArticles}
                                                l10n={this.props.l10n}
                                                currentPageNumber={this.state.pageNo}
                                                totalPagesNumber={this.state.totalPageCount}
                                                url={this.props.searchResultPageProps.url}
                                                showHiddenLinks={false}
                                            />
                                        }
                                    </>
                                }

                                {this.isRegularSearchResultPage &&
                                    <>
                                        {this.state.hasFlexibleSets ? (
                                            <>
                                                <ArticleTileGrid
                                                    currencyInformation={this.props.searchResultPageProps.currencyInformation}
                                                    viewType={this.props.searchResultPageProps.filterContentViewModel.viewType}
                                                    isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                                    isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                                    culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                                    portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                                    isFilterDropDownOpen={!!this.state.activeDropDownFilterName}
                                                    articles={this.state.salesDesignationViews.slice(0, articleGridSplitIndex)}
                                                    category={null}
                                                    l10n={this.props.l10n}
                                                    kiosk={this.props.searchResultPageProps.filterContentViewModel.kiosk}
                                                    gridRef={this.firstGridRef}
                                                    addItemOriginToPdpLink={true}
                                                    gridNumber={0}
                                                    globalState={this.globalState}
                                                    singleArticleView={this.state.salesDesignationViews && this.state.salesDesignationViews.length === 1}
                                                    magicLink={this.props.searchResultPageProps.magicLink}
                                                />

                                                <FlexibleSetListWrapper
                                                    flexibleSetContext={this.state.flexibleSetContext}
                                                    culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                                    portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                                    searchTerm={this.props.searchResultPageProps.filterContentViewModel.searchTerm}
                                                    magicLink={this.props.searchResultPageProps.magicLink}
                                                    l10n={this.props.l10n}
                                                    kiosk={this.props.searchResultPageProps.filterContentViewModel.kiosk}
                                                    isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                                    isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                                    searchPageStyle={this.props.searchResultPageProps.style}
                                                />
                                                
                                                {showSecondArticleGrid &&
                                                    <>
                                                        <div className={classNames(styles.article_grid_divider)}>
                                                            <span className={styles.headline} 
                                                                dangerouslySetInnerHTML={{ __html: this.articleGridDividerHeadline }}>
                                                            </span>
                                                        </div>
                                                        <ArticleTileGrid
                                                            currencyInformation={this.props.searchResultPageProps.currencyInformation}
                                                            viewType={this.props.searchResultPageProps.filterContentViewModel.viewType}
                                                            isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                                            isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                                            culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                                            portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                                            isFilterDropDownOpen={!!this.state.activeDropDownFilterName}
                                                            articles={this.state.salesDesignationViews.slice(articleGridSplitIndex)}
                                                            category={null}
                                                            l10n={this.props.l10n}
                                                            kiosk={this.props.searchResultPageProps.filterContentViewModel.kiosk}
                                                            gridRef={this.secondGridRef}
                                                            addItemOriginToPdpLink={true}
                                                            gridNumber={1}
                                                            globalState={this.globalState}
                                                            singleArticleView={
                                                                this.state.salesDesignationViews && this.state.salesDesignationViews.length === (articleGridSplitIndex + 1)
                                                            }
                                                            magicLink={this.props.searchResultPageProps.magicLink}
                                                        />
                                                    </>
                                                }
                                            </>
                                        ) : (
                                            <ArticleTileGrid
                                                currencyInformation={this.props.searchResultPageProps.currencyInformation}
                                                viewType={this.props.searchResultPageProps.filterContentViewModel.viewType}
                                                isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                                isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                                culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                                portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                                isFilterDropDownOpen={!!this.state.activeDropDownFilterName}
                                                articles={this.state.salesDesignationViews}
                                                category={null}
                                                l10n={this.props.l10n}
                                                kiosk={this.props.searchResultPageProps.filterContentViewModel.kiosk}
                                                gridRef={this.singleGridRef}
                                                addItemOriginToPdpLink={true}
                                                gridNumber={0}
                                                globalState={this.globalState}
                                                singleArticleView={this.state.salesDesignationViews && this.state.salesDesignationViews.length === 1}
                                                magicLink={this.props.searchResultPageProps.magicLink}
                                            />
                                        )}
                                        {this.state.totalResultCount > 0 &&
                                            <Paging
                                                viewedArticleNumber={this.state.pageEndOffset}
                                                totalArticleCount={this.state.totalResultCount}
                                                waitingForResponse={this.state.waitingForResponse}
                                                onLoadMore={this.handleLoadArticles}
                                                l10n={this.props.l10n}
                                                currentPageNumber={this.state.pageNo}
                                                totalPagesNumber={this.state.totalPageCount}
                                                url={this.props.searchResultPageProps.url}
                                                showHiddenLinks={false}
                                            />
                                        }
                                    </>
                                }

                                {this.isTeaserSearchResultPage && 
                                    <>
                                        {this.state.hasFlexibleSets &&
                                            <FlexibleSetListWrapper
                                                flexibleSetContext={this.state.flexibleSetContext}
                                                culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                                portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                                searchTerm={this.props.searchResultPageProps.filterContentViewModel.searchTerm}
                                                magicLink={this.props.searchResultPageProps.magicLink}
                                                l10n={this.props.l10n}
                                                kiosk={this.props.searchResultPageProps.filterContentViewModel.kiosk}
                                                isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                                isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                                searchPageStyle={this.props.searchResultPageProps.style}
                                            />
                                        }

                                        <TeaserView
                                            articleLists={this.props.searchResultPageProps.articleLists}
                                            currencyInformation={this.props.searchResultPageProps.currencyInformation}
                                            kiosk={this.props.searchResultPageProps.filterContentViewModel.kiosk}
                                            isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                            portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                            culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                            isGlobal={this.props.searchResultPageProps.filterContentViewModel.isGlobal}
                                            l10n={this.props.l10n}
                                            globalState={this.globalState}
                                            articleTilesAjaxCaller={this.articleTilesAjaxCaller}
                                            magicLink={this.props.searchResultPageProps.magicLink}
                                        />
                                    </>
                                }
                            </div>

                            {this.props.searchResultPageProps.bottomSuggestionLinks?.suggestionLinks.length > 0 ? (
                                <SuggestionLinks
                                    suggestionLink={this.props.searchResultPageProps.bottomSuggestionLinks}
                                    isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                    isTablet={this.props.searchResultPageProps.filterContentViewModel.isTablet}
                                    portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                    culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                    position={'bottom'}
                                    magicLink={this.props.searchResultPageProps.magicLink}
                                    seeAllResultsL10n={this.props.l10n.seeAllResults}
                                />
                            ) : this.props.searchResultPageProps.topSuggestionLinks?.suggestionLinks.length > 0 && (
                                <SuggestionLinks
                                    suggestionLink={this.props.searchResultPageProps.topSuggestionLinks}
                                    isMobile={this.props.searchResultPageProps.filterContentViewModel.isMobile}
                                    isTablet={this.props.searchResultPageProps.filterContentViewModel.isTablet}
                                    portal={this.props.searchResultPageProps.filterContentViewModel.portal}
                                    culture={this.props.searchResultPageProps.filterContentViewModel.culture}
                                    position={'bottom'}
                                    magicLink={this.props.searchResultPageProps.magicLink}
                                    seeAllResultsL10n={this.props.l10n.seeAllResults}
                                />
                            )}

                            {!this.isEmptySearchResultPage &&
                                <BottomSearchbox
                                    style={this.props.searchResultPageProps.style}
                                    l10n={this.props.l10n}
                                />
                            }

                            {(!this.props.searchResultPageProps.filterContentViewModel.isEsApp && this.props.searchResultPageProps.languageSwitcherFragment) &&
                                <LanguageSwitcher
                                    languageSwitcherProps={this.props.searchResultPageProps.languageSwitcherFragment}
                                    l10n={this.props.l10n}
                                    magicLink={this.props.searchResultPageProps.magicLink}
                                />
                            }
                        </div>
                    </ArticleTileContextProvider>
                </SortContextProvider>
            </CollectionLineContextProvider>
        );
    }

    private handleLoadArticles(focusFirstTileInNewPage?: boolean) {
        this.setState({ waitingForResponse: true })
        this.articleTilesAjaxCaller
            .loadMoreArticles(
                this.state.pageNo + 1,
                this.props.searchResultPageProps.filterContentViewModel.kiosk,
                this.props.searchResultPageProps.filterContentViewModel.viewType,
                true,
                this.props.searchResultPageProps.filterContentViewModel.customerType,
                this.props.searchResultPageProps.filterContentViewModel.enableAISorting
            )
            .then((data) => {
                if (data && Array.isArray(data.articles)) {
                    this.setState({
                        pageNo: this.state.pageNo + 1,
                        pageEndOffset: data.pageEndOffset,
                        salesDesignationViews: this.state.salesDesignationViews.concat(data.articles)
                    }, () => {
                        const scrollPos = helper.calculateScrollDistance(
                            this.state.pageEndOffset,
                            this.state.salesDesignationViews);
                        this.setState({ scrollPosition: scrollPos });
                        this.bookmarkController.updateArticleList(data.articles);

                        // call mpc for tp upgrade
                        this.touchPointFactory.createTouchPointsBatch(data.articles);
                        
                        this.tabNav.focusFirstTileInNewPage(
                            (this.state.hasFlexibleSets ? this.secondGridRef : this.singleGridRef), focusFirstTileInNewPage, data.articles);
                    });
                }
                this.setState({ waitingForResponse: false })
            });
    }

    private handleMissingTabNavAttOnFirstArticleGridRef() {
        if (this.state.hasFlexibleSets && this.firstGridRef.current !== null) {
            setTimeout(() => {
                this.firstGridRef.current!.querySelectorAll<HTMLDivElement>('.fas_article_tile').forEach((tile) => this.tabNav.setFocusAtt(tile));
            }, 100);
        }
    }

    private handleTrackingEvents() {
        const searchTerm = this.props.searchResultPageProps.originalSearchTerm;

        if (this.props.searchResultPageProps.enableKraken && this.props.searchResultPageProps.enableSearchResultEventForKraken) {
            let eventArticles: ISearchResultEventItem[] = [];
            let eventFlexSets: ISearchResultEventItem[] = [];
            let totalProductResultCount = 0;
            let currencyIsoCode = '';

            if (!(this.isEmptySearchResultPage || this.isTeaserSearchResultPage)) {
                eventArticles = this.state.salesDesignationViews.map((view) => {
                    const item: ISearchResultEventItem = {
                        id: view.defaultSalesArticleNo,
                        type: 'product',
                        name: view.description,
                        category: view.navigationKey,
                        numItems: 1,
                        value: view.minimalPrice.price.grossValue,
                    };
                    return item;
                });

                if (this.state.hasFlexibleSets) {
                    eventFlexSets =
                        this.state.flexibleSetContext.flexibleSets.map(
                            (set) => {
                                const item: ISearchResultEventItem = {
                                    id: set.fsCode,
                                    type: 'product',
                                    name: set.salesDesignation,
                                    category: 'Flexible_Sets',
                                    numItems: 1,
                                    value: set.price.price.gross.toString(),
                                };
                                return item;
                            },
                        );
                }

                totalProductResultCount =
                    (this.props.searchResultPageProps.filterContentViewModel
                        .filterModel.matchingArticleCount ?? 0) +
                    eventFlexSets.length;
                currencyIsoCode = this.props.searchResultPageProps.currencyInformation?.isoCode ?? '';
            }

            // edge case: empty search result type containing flexible sets 
            if (this.isEmptySearchResultPage && this.state.hasFlexibleSets) {
                eventFlexSets =
                    this.state.flexibleSetContext.flexibleSets.map(
                        (set) => {
                            const item: ISearchResultEventItem = {
                                id: set.fsCode,
                                type: 'product',
                                name: set.salesDesignation,
                                category: 'Flexible_Sets',
                                numItems: 1,
                                value: set.price.price.gross.toString(),
                            };
                            return item;
                        },
                    );
                totalProductResultCount = eventFlexSets.length;
                currencyIsoCode = this.state.flexibleSetContext.setCurrencyInformation.isoCode;
            }

            const eventData: ISearchResultEventData = {
                contents: [...eventArticles, ...eventFlexSets],
                currency: currencyIsoCode,
                searchTerm: searchTerm,
                value: 0,
                searchCount: totalProductResultCount,
                searchPageType: this.props.searchResultPageProps.metaTagPageType,
            };

            AtsTrackingHelper.publishSearchResultEvent(eventData);
        } else {
            // this part can be cleaned up after FT "EnableKraken" and "EnableSearchResultEventForKraken" are enabled by default
            if (this.isEmptySearchResultPage || this.isTeaserSearchResultPage) return;

            const salesArticleNos = this.state.salesDesignationViews.map((view) => view.defaultSalesArticleNo);
            const contentArticleItems = this.state.salesDesignationViews.map((view) => {               
                const item: ThirdPartyEvents.ITikTokContentItem = {
                    content_id: view.defaultSalesArticleNo,
                    content_type: 'product',
                    content_name: view.description,
                    content_category: view.navigationKey,
                    num_items: 1,
                    value: view.minimalPrice.price.grossValue
                }
                return item;
            });
            
            let contentFlexSets: ThirdPartyEvents.ITikTokContentItem[] = [];
            if (this.state.hasFlexibleSets) {
                contentFlexSets = this.state.flexibleSetContext.flexibleSets.map(set => {
                    const item: ThirdPartyEvents.ITikTokContentItem = {
                        content_id: set.fsCode,
                        content_type: 'product',
                        content_name: set.salesDesignation,
                        content_category: 'Flexible_Sets',
                        num_items: 1,
                        value: set.price.price.gross.toString()
                    }
                    return item;
                });
            }
            const contentItems = [...contentArticleItems, ...contentFlexSets];
            
            const metaEvent: ThirdPartyEvents.IMetaSearchEvent = {
                search_string: searchTerm,
                content_ids: salesArticleNos
            };
            const tikTokEvent: ThirdPartyEvents.ITikTokSearchEvent = {
                contents: contentItems,
                currency: this.props.searchResultPageProps.currencyInformation?.isoCode ?? '',
                search_string: searchTerm,
                value: 0
            };
            ThirdPartyEvents.sendMetaSearchEvent(metaEvent);
            ThirdPartyEvents.sendTikTokSearchEvent(tikTokEvent);
        }
    }
}
